// Route: /catalog — ПУБЛИЧНЫЙ каталог курсов.
// URL state: ?area=, ?purpose=, ?origin=, ?locale=, ?exam=, ?pricing=, ?q=,
//            ?sort=popular|new, ?scope=mine, ?preset=<id>, ?page=N
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему у страницы каталога есть НОМЕР, и почему он в запросе
// ─────────────────────────────────────────────────────────────────────────────
// Кнопка «Показать ещё» адреса не меняет, поэтому дальше первой порции идти было
// НЕКУДА: ни поделиться местом в каталоге, ни обойти его роботом. В серверном
// HTML не было ни одной ссылки на курс — карта сайта сообщала об их
// существовании и не передавала им ничего больше.
//
// Номер живёт в запросе, а не сегментом пути, потому что остальное состояние
// списка уже там: сегмент завёл бы вторую модель состояния рядом с первой, всё
// равно таща `?area=…` хвостом, занял бы слово `page` в пространстве слагов
// курсов и потребовал бы отдельной обработки в языковом префиксе. Полное
// обоснование и правило адреса — `@studyhub/shared/utils/catalog-pagination`.
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему адрес не `/courses/catalog`
// ─────────────────────────────────────────────────────────────────────────────
// `lib/protected-routes.ts` закрывает ВЕСЬ префикс `/courses`. Публичная витрина
// под ним потребовала бы записи в `PUBLIC_EXCEPTION_PREFIXES` — механизме,
// заведённом под точечные случаи вроде `/audio-recaps/share/`. Целый публичный
// раздел через список исключений — мина: каждый новый вложенный роут по
// умолчанию оказывался бы публичным или закрытым не туда. Поэтому публичная
// зона вынесена ЗА пределы закрытого префикса, а `/courses/catalog` остался
// редиректом сюда (`routes/courses/catalog.tsx`).
//
// ─────────────────────────────────────────────────────────────────────────────
// Один каталог вместо двух разделов
// ─────────────────────────────────────────────────────────────────────────────
// Раньше существовали «Каталог курсов» (`/courses/templates`, курсы авторов) и
// «Шаблоны программ» (`/study-plan/templates`, пресеты платформы под экзамены).
// Для пользователя это было одно и то же — «дай готовую программу», — а разница
// между ними (копия готового курса против генерации по каркасу) относится к
// СПОСОБУ производства, а не к разделу навигации. Поэтому источники склеены в
// один грид с универсальными фильтрами, а разница объясняется на карточке.
//
// Фильтрует и сортирует СЕРВЕР (`courseTemplates.catalogList`, `publicProcedure`):
// выдача склеена из двух таблиц UNION-ом, и резать её на клиенте нельзя —
// вторая страница перемешала бы источники. Гостю сервер дополнительно сужает
// выдачу до `status='published' AND is_listed` и отдаёт `enrolledCourseId: null`.
//
// Пять состояний §10A: loading (скелетон-сетка) · empty (сброс фильтров) ·
// error (`ErrorState` + retry) · offline (`OfflineDetailState` — каталог живёт
// только онлайн, локальной копии нет и обещать её нельзя).
//
// ЗАПИСИ С ЭТОГО ЭКРАНА БОЛЬШЕ НЕТ: карточка потеряла кнопку «Начать курс» и
// целиком ведёт на страницу курса, где живёт полноценный старт (вход, цена,
// оплата, диалог генерации). Поэтому здесь нет ни мутации записи, ни гейта
// входа, ни онлайн-гейта — им нечего охранять. Единственный остаток прежнего
// потока — `?preset=`: это ВНЕШНИЙ deep-link на программу платформы (лендинг,
// письмо, редирект `/study-plan/templates`, конструктор курса), и обрывать его
// значило бы поломать чужие ссылки.
//
// URL здесь — состояние экрана: фильтры, поиск, скоуп и открытая программа
// платформы (`?preset=`). Прокрутка при таких переключениях НЕ съезжает вверх —
// это решает общее правило роутера (`main.tsx`, `scrollRestoration`): путь не
// сменился, значит экран прежний и трогать прокрутку не за что. Локальных
// `resetScroll: false` тут нет намеренно — политика прокрутки живёт в одном
// месте на всё приложение, иначе она есть в каталоге и отсутствует в остальных
// списках.

import {
	CATALOG_MAX_PAGE,
	catalogPageOffset,
	catalogPageSearch,
} from "@studyhub/shared/utils/catalog-pagination";
import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { IconArrowLeft, IconSchool } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCourseCatalog, useCourseCatalogFacets } from "@/components/courses/catalog/_api";
import {
	ANY,
	CatalogFilters,
	type CatalogFiltersValue,
} from "@/components/courses/catalog/catalog-filters";
import { CatalogBody, CatalogItemListJsonLd } from "@/components/courses/catalog/catalog-grid";
import { catalogPageToWrite } from "@/components/courses/catalog/catalog-page-anchor";
import { CatalogPagination } from "@/components/courses/catalog/catalog-pagination";
import { MyTemplatesPanel } from "@/components/courses/templates/templates-mine-panel";
import { PageMeta } from "@/components/seo/PageMeta";
import { LIST_PAGE_SHELL, ListToolbar } from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { GeneratePlanDialog } from "@/components/study-plan/generate-dialog/generate-plan-dialog";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useSession } from "@/hooks/use-session";
import { useViewMode } from "@/hooks/use-view-mode";
import { useCommerceVisible } from "@/lib/module-availability/commerce";

const SCOPES = ["catalog", "mine"] as const;
type CatalogScope = (typeof SCOPES)[number];

const searchSchema = z.object({
	scope: z.enum(SCOPES).optional(),
	area: z.string().optional(),
	purpose: z.string().optional(),
	origin: z.string().optional(),
	locale: z.string().optional(),
	exam: z.string().optional(),
	/** Ось «цена»: `free` | `paid`. Отсутствие значения = обе группы. */
	pricing: z.enum(["free", "paid"]).optional(),
	q: z.string().optional(),
	sort: z.enum(["popular", "new"]).optional(),
	/**
	 * Номер страницы витрины. Первая — БЕЗ параметра: `?page=1` был бы вторым
	 * адресом той же страницы, то есть дублем в индексе.
	 *
	 * `catch(undefined)` вместо строгой проверки: адрес приходит извне, и на
	 * `?page=abc` каталог обязан открыться, а не упасть в ошибку роутера.
	 */
	page: z.coerce.number().int().min(2).max(CATALOG_MAX_PAGE).optional().catch(undefined),
	/** Открыть создание курса по программе платформы (deep-link на пресет). */
	preset: z.string().optional(),
});

export const Route = createFileRoute("/catalog")({
	validateSearch: tolerantSearch(searchSchema),
	component: CourseCatalogPage,
	pendingComponent: PagePendingFallback,
});

function CourseCatalogPage(): React.ReactElement {
	const { t } = useTranslation("courses");
	const intro = useSectionIntro("course-templates");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const { isAuthenticated } = useSession();
	// Гость скоупа «Мои шаблоны» не имеет вовсе — не «видит пустой», а не видит
	// переключателя: показать вкладку, которая гарантированно открывает модалку
	// входа, значит поставить приманку.
	const scope: CatalogScope = isAuthenticated && search.scope === "mine" ? "mine" : "catalog";

	// Гостю скоуп не нужен: «Мои шаблоны» требуют аккаунта. Не рендерим вовсе,
	// а не блокируем — выключенная кнопка обещает то, чего у гостя нет.
	const scopeControl = isAuthenticated ? (
		<SegmentedControl
			aria-label={t("catalog.scopeLabel")}
			value={scope}
			onValueChange={(next) =>
				void navigate({
					// `catalog` — состояние по умолчанию, в URL его не пишем.
					search: (prev) => ({ ...prev, scope: next === "catalog" ? undefined : next }) as never,
					replace: true,
				})
			}
			options={[
				{ value: "catalog", label: t("catalog.scopeCatalog") },
				{ value: "mine", label: t("catalog.scopeMine") },
			]}
		/>
	) : null;

	return (
		<div className={LIST_PAGE_SHELL}>
			{/* Canonical страницы 2 — она сама, а не первая: у неё СВОИ курсы, и
			    заявка «индексируйте вон ту» выбросила бы их содержимое из выдачи.
			    Фильтры в canonical не входят намеренно — см. `PageMeta.search`. */}
			<PageMeta
				title={t("catalog.seo.title")}
				description={t("catalog.seo.description")}
				path="/catalog"
				search={catalogPageSearch(search.page ?? 1)}
			/>

			{/* Возврат в раздел «Обучение» — только для своего пользователя: гостю
			    некуда возвращаться, каталог для него самостоятельная страница. */}
			{isAuthenticated ? (
				<div className="flex items-center gap-2">
					<Button asChild variant="ghost" size="sm" className="-ml-2 cursor-pointer">
						<Link to="/courses" preload="viewport">
							<IconArrowLeft size={14} />
							{t("nav.courses", { ns: "layout" })}
						</Link>
					</Button>
				</div>
			) : null}

			<SectionHeader
				icon={<IconSchool stroke={1.75} />}
				title={t("catalog.title")}
				description={t("catalog.subtitle")}
				actions={<SectionIntroReopen intro={intro} />}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное. Гостю не показывается вовсе — состояние
			    хранится за авторизацией (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{/* «Мои шаблоны» — не фильтр каталога, а личный скоуп: там видны
			    черновики и снятые с публикации, которым в публичной витрине не
			    место. Поэтому переключатель, а не значение фильтра «источник».
			    Живёт он в ряду фильтров, а не отдельной строкой под шапкой:
			    собственная строка ради двух кнопок съедала высоту до первой
			    карточки, а места в ряду хватило после удаления фильтра языка. */}
			{scope === "mine" ? (
				<MyTemplatesPanel scopeControl={scopeControl} />
			) : (
				<CatalogScopeView scopeControl={scopeControl} />
			)}
		</div>
	);
}

/** Витрина каталога: универсальные фильтры + единый грид с записью. */
function CatalogScopeView({ scopeControl }: { scopeControl: React.ReactNode }): React.ReactElement {
	const { t } = useTranslation("courses");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const online = useOnlineStatus();
	const [view, setView] = useViewMode("course-catalog");
	const commerceVisible = useCommerceVisible();

	const filters: CatalogFiltersValue = {
		subjectArea: (search.area ?? ANY) as CatalogFiltersValue["subjectArea"],
		purpose: (search.purpose ?? ANY) as CatalogFiltersValue["purpose"],
		origin: (search.origin ?? ANY) as CatalogFiltersValue["origin"],
		educationSubsystem: search.exam ?? ANY,
		// Там, где продаж не видно (приложение из App Store, guideline 3.1.1),
		// ось цены не существует: выдача всегда бесплатная, и `?pricing=paid` из
		// чужой ссылки её не переключает.
		pricing: commerceVisible ? ((search.pricing ?? ANY) as CatalogFiltersValue["pricing"]) : ANY,
		query: search.q ?? "",
		sort: search.sort ?? "popular",
	};

	const page = search.page ?? 1;
	const catalog = useCourseCatalog({
		sort: filters.sort,
		page,
		...(filters.subjectArea === ANY ? {} : { subjectArea: filters.subjectArea }),
		...(filters.purpose === ANY ? {} : { purpose: filters.purpose }),
		...(filters.origin === ANY ? {} : { origin: filters.origin }),
		...(filters.educationSubsystem === ANY
			? {}
			: { educationSubsystem: filters.educationSubsystem }),
		...(commerceVisible
			? filters.pricing === ANY
				? {}
				: { pricing: filters.pricing }
			: { pricing: "free" as const }),
		...(filters.query.trim() === "" ? {} : { query: filters.query.trim() }),
	});

	const facets = useCourseCatalogFacets({
		...(filters.query.trim() === "" ? {} : { query: filters.query.trim() }),
	});

	// Открытая программа платформы ВЫВОДИТСЯ из URL, а не хранится в state:
	// `?preset=` приходит СНАРУЖИ (лендинг, письмо, редирект
	// `/study-plan/templates`, конструктор курса), и эффект «увидел id → положил
	// в state» дал бы лишний рендер и класс багов с рассинхроном URL и диалога.
	// Сам каталог этот параметр больше не выставляет: кнопки старта на карточке
	// нет, курс по программе создаётся со страницы курса.
	const preset = useMemo(
		() =>
			search.preset
				? (catalog.items.find((item) => item.origin === "official" && item.id === search.preset) ??
					null)
				: null,
		[search.preset, catalog.items],
	);

	// Подсистемы экзамена, реально встречающиеся в выдаче: захардкоженный список
	// показывал бы фильтры, которые ничего не находят.
	const subsystems = useMemo(
		() =>
			[
				...new Set(
					catalog.items
						.map((item) => item.educationSubsystem)
						.filter((value): value is string => Boolean(value)),
				),
			].toSorted(),
		[catalog.items],
	);

	// Подгрузка отражается в адресе: долистал до третьей — адрес говорит `?page=3`,
	// и ссылкой на это место можно поделиться. `replace`, а не переход: иначе
	// каждое нажатие «Показать ещё» кладёт запись в историю, и «назад» заставляет
	// человека отщёлкивать их по одной вместо возврата туда, откуда он пришёл.
	//
	// КОГДА писать — решает `catalogPageToWrite`, и это не «отражение ≠ адрес»:
	// пока якорь догоняет только что изменившийся адрес, запись отменила бы
	// навигацию человека. Обратную сторону — «адрес изменили снаружи, лента
	// начинается заново» — разбирает `useCourseCatalog`.
	// Прокрутка к началу выдачи при СМЕНЕ страницы — только на реальную смену
	// якоря, а не на каждое изменение адреса: «Показать ещё» тоже пишет в адрес,
	// и прыжок вверх после подгрузки отбросил бы человека от того места, ради
	// которого он и нажимал кнопку.
	const listTopRef = useRef<HTMLDivElement>(null);
	const scrolledForAnchorRef = useRef(catalog.anchorPage);
	useEffect(() => {
		if (scrolledForAnchorRef.current === catalog.anchorPage) return;
		scrolledForAnchorRef.current = catalog.anchorPage;
		listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [catalog.anchorPage]);

	const pageToWrite = catalogPageToWrite({
		address: page,
		anchor: catalog.anchorPage,
		reflected: catalog.reflectedPage,
	});
	useEffect(() => {
		if (pageToWrite === null) return;
		void navigate({
			search: (prev) => ({ ...prev, page: pageToWrite <= 1 ? undefined : pageToWrite }) as never,
			replace: true,
		});
	}, [pageToWrite, navigate]);

	function patchFilters(patch: Partial<CatalogFiltersValue>): void {
		void navigate({
			search: (prev) => {
				// Смена фильтра начинает ленту заново: «страница 3» новой выдачи —
				// это другие курсы, и оставить номер значило бы уронить человека в
				// середину списка, которого он ещё не видел.
				const next: Record<string, string | number | undefined> = { ...prev, page: undefined };
				const map: Record<keyof CatalogFiltersValue, string> = {
					subjectArea: "area",
					purpose: "purpose",
					origin: "origin",
					educationSubsystem: "exam",
					pricing: "pricing",
					query: "q",
					sort: "sort",
				};
				for (const [key, value] of Object.entries(patch)) {
					const param = map[key as keyof CatalogFiltersValue];
					// `any`, пустая строка и дефолтная сортировка в URL не пишутся:
					// ссылка на каталог должна выглядеть как `/catalog`.
					next[param] =
						!value || value === ANY || (param === "sort" && value === "popular")
							? undefined
							: String(value);
				}
				return next as never;
			},
			replace: true,
		});
	}

	const hasFilters =
		filters.subjectArea !== ANY ||
		filters.purpose !== ANY ||
		filters.origin !== ANY ||
		filters.educationSubsystem !== ANY ||
		filters.pricing !== ANY ||
		filters.query.length > 0;

	const filtersActive =
		filters.subjectArea !== ANY ||
		filters.purpose !== ANY ||
		filters.origin !== ANY ||
		filters.educationSubsystem !== ANY ||
		filters.pricing !== ANY ||
		filters.sort !== "popular";

	return (
		<div className="space-y-section">
			{/* Микроразметка списка — только по УЖЕ загруженным карточкам: ItemList
			    обязан описывать то, что реально на экране, иначе это расхождение
			    разметки и контента. Позиции — сквозные по ленте: лента могла быть
			    начата не с первой страницы, и там первый показанный курс не первый
			    в каталоге. */}
			{catalog.items.length > 0 ? (
				<CatalogItemListJsonLd
					items={catalog.items}
					startPosition={catalogPageOffset(catalog.anchorPage) + 1}
				/>
			) : null}

			<ListToolbar
				// На mobile ряд фильтров заменён кнопкой-шторкой, поэтому скоуп
				// там показывает слот сегментов (вторая строка тулбара).
				segments={<div className="lg:hidden">{scopeControl}</div>}
				searchLayout="compact"
				search={{
					value: filters.query,
					onChange: (query) => patchFilters({ query }),
					placeholder: t("catalog.filters.search"),
				}}
				filters={
					<>
						<CatalogFilters
							value={filters}
							subsystems={subsystems}
							hasPaid={commerceVisible && facets.hasPaid}
							count={facets.count}
							onChange={patchFilters}
						/>
						{/* Скоуп — последним в ряду, вплотную к переключателю вида:
						    это не фильтр выдачи, а выбор того, ЧТО показываем. */}
						{scopeControl}
					</>
				}
				filtersSheet={
					<CatalogFilters
						value={filters}
						subsystems={subsystems}
						hasPaid={commerceVisible && facets.hasPaid}
						count={facets.count}
						onChange={patchFilters}
						stacked
					/>
				}
				filtersActive={filtersActive}
				view={view}
				onViewChange={setView}
			/>

			{/* Якорь прокрутки: смена номерной страницы обязана вернуть человека к
			    началу выдачи. Без этого он оставался там, где кликнул пагинацию —
			    у подвала, — и новая страница выглядела пустой, потому что её
			    первые карточки были выше экрана (замечание владельца 2026-08-16).
			    `scroll-mt` — запас под липкую шапку, иначе первый ряд карточек
			    уезжает под неё. */}
			<div ref={listTopRef} className="scroll-mt-[calc(var(--topbar-height)+1rem)]" />

			<CatalogBody
				online={online}
				isLoading={catalog.isLoading}
				isError={catalog.isError}
				items={catalog.items}
				hasFilters={hasFilters}
				view={view}
				onRetry={() => catalog.refetch()}
				onResetFilters={() =>
					patchFilters({
						subjectArea: ANY,
						purpose: ANY,
						origin: ANY,
						educationSubsystem: ANY,
						pricing: ANY,
						query: "",
					})
				}
			/>

			{/* Две дороги к одному и тому же, и обе нужны. Кнопка — для человека:
			    добавляет порцию, не сбрасывая прокрутку. Ссылки — для всех
			    остальных: у страницы появляется адрес, поэтому её можно обойти
			    роботом, открыть в новой вкладке и отправить ссылкой. */}
			{catalog.hasMore || catalog.pageCount > 1 ? (
				<div className="flex flex-col items-center gap-3">
					{catalog.hasMore ? (
						<Button
							variant="outline"
							className="cursor-pointer pointer-coarse:h-11"
							loading={catalog.isLoadingMore}
							onClick={() => catalog.loadMore()}
						>
							{t("catalog.loadMore")}
						</Button>
					) : null}
					<CatalogPagination
						current={catalog.reflectedPage}
						pageCount={catalog.pageCount}
						search={search}
					/>
				</div>
			) : null}

			{/* Пояснение под гридом: страница индексируется, и поисковику нужен
			    текст, а не только сетка карточек. Прячем при активных фильтрах —
			    там пользователь уже знает, что ищет, и текст только удлиняет
			    путь к результатам. */}
			{hasFilters ? null : (
				<section className="space-y-2 border-t border-border pt-6">
					<h2 className="text-sm font-semibold text-foreground">{t("catalog.seo.aboutTitle")}</h2>
					<p className="max-w-prose text-xs leading-relaxed text-muted-foreground">
						{t("catalog.seo.aboutBody")}
					</p>
				</section>
			)}

			{/* Выбор программы платформы = создание курса с prefill (title + goal). */}
			<GeneratePlanDialog
				open={preset !== null}
				onOpenChange={(next) => {
					if (next) return;
					void navigate({
						search: (prev) => ({ ...prev, preset: undefined }) as never,
						replace: true,
					});
				}}
				prefilledTemplate={
					preset
						? { id: preset.id, title: preset.title, goal: preset.description ?? preset.title }
						: undefined
				}
			/>
		</div>
	);
}
